import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	TextField,
	Autocomplete,
	Grid,
	Checkbox,
	CircularProgress,
	Chip,
	Typography,
	Paper,
	IconButton,
	Tooltip,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SendIcon from "@mui/icons-material/Send";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios, { AxiosError } from "axios";
import { RootState } from "../../store/Store";
import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../store/actions";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import {
	getAllSkills,
	getEmployeeSkillDocument,
	skillDocumentUpload,
	skillstoemployee,
	viewActiveUsers,
	deleteSkillDocument,
	documentAssignmentCount,
} from "../../Service/Apis";
import Loading from "../../components/Loading/Loading";
import {
	ToastVariant,
	useCustomToast,
} from "../../components/Methods/SnackBar";
import SkillDocumentCommentBox from "../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";

interface Skill {
	skill_id: string;
	skill_name: string;
	created_at: string;
}

interface Employee {
	id: string;
	employee_code: string;
	first_name: string;
	last_name: string;
	gender: string;
	email: string;
	roles: string[];
	teams: string[];
	designation: string;
	date_of_birth: string;
	date_of_joining: string;
	date_of_confirmation: string;
	date_of_resignation: string | null;
	date_of_exit: string | null;
	is_active: boolean;
	is_superuser: boolean;
	is_staff: boolean;
	last_login: string;
}

interface Document {
	document_id: string;
	document_name: string;
}

interface FormValues {
	skill: Skill | null;
	employee: Employee[];
	date: Date | null;
	comment: string;
}

const validationSchema = Yup.object({
	skill: Yup.object().nullable().required("Skill is required"),
	employee: Yup.array().min(1, "At least one employee is required"),
	date: Yup.mixed()
		.nullable()
		.required("Completion date is required")
		.test(
			"is-valid-date",
			"Please enter a valid date",
			(value) =>
				value !== null &&
				value !== undefined &&
				!isNaN(new Date(value as string).getTime())
		),
	comment: Yup.string().nullable(),
});

const SkillDocument = () => {
	const [comment, setComment] = useState<string>("");
	const [skills, setSkills] = useState<Skill[]>([]);
	const [employees, setEmployees] = useState<Employee[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedSkillId, setSelectedSkillId] = useState("");
	const [documentLoading, setDocumentLoading] = useState(false);
	const [isSkillLoading, setIsSkillLoading] = useState(false);
	const [isEmployeeLoading, setIsEmployeeLoading] = useState(false);
	const [isDocumentUploading, setIsDocumentUploading] = useState(false);
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
	const [isDeletingDocument, setIsDeletingDocument] = useState(false);
	const [assignedEmployeeCount, setAssignedEmployeeCount] = useState<number | null>(null);
	const [isFetchingCount, setIsFetchingCount] = useState(false);

	const showToast = useCustomToast();
	const handleShowToast = (
		message: string,
		variant: ToastVariant,
		isCloseable: boolean
	) => {
		showToast(message, variant, isCloseable);
	};

	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();

	const fetchSkills = async () => {
		try {
			setIsSkillLoading(true);
			dispatch2(fetchData(`${getAllSkills}`))
				.then((res) => {
					if (res && Array.isArray(res)) {
						setSkills(res);
					}
				})
				.catch(() => {
					handleShowToast("Error fetching skills", "error", true);
				})
				.finally(() => {
					setIsSkillLoading(false);
				});
		} catch (e) {
			setIsSkillLoading(false);
		}
	};

	const fetchEmployees = async () => {
		try {
			setIsEmployeeLoading(true);
			dispatch2(fetchData(`${viewActiveUsers}`))
				.then((res) => {
					if (res && Array.isArray(res)) {
						setEmployees(res);
					}
				})
				.catch(() => {})
				.finally(() => {
					setIsEmployeeLoading(false);
				});
		} catch (e) {
			setIsEmployeeLoading(false);
		}
	};

	const getData = async () => {
		try {
			setIsLoading(true);
			await fetchSkills();
			await fetchEmployees();
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getData();
	}, []);

	const fetchDocuments = async (skillId: string) => {
		try {
			setDocumentLoading(true);
			const data = { skill_id: skillId };
			dispatch2(fetchData(`${getEmployeeSkillDocument}`, data))
				.then((res) => {
					if (res && Array.isArray(res)) {
						setDocuments(res);
						setSelectedDocuments([]);
					}
				})
				.catch(() => {
					handleShowToast("Error fetching documents", "error", true);
				});
		} catch (e) {
			console.error(e);
		} finally {
			setDocumentLoading(false);
		}
	};

	const handleSubmit = async (values: FormValues, { resetForm }: any) => {
		if (!values.skill) return;

		if (documents.length === 0) {
			handleShowToast("Please upload a document", "error", true);
			return;
		}

		if (selectedDocuments.length === 0) {
			handleShowToast("Please select at least one document", "error", true);
			return;
		}

		setIsFormSubmitting(true);
		const formData = new FormData();
		formData.append("skill_id", values.skill.skill_id);
		values.employee.forEach((emp) => {
			formData.append("employee_ids", emp.id);
		});
		selectedDocuments.forEach((docId) =>
			formData.append("document_ids", docId)
		);
		formData.append("hr_completion_date", values.date?.toISOString() || "");
		formData.append("comment", comment || "");

		try {
			await axios.post(skillstoemployee, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: `Bearer ${localStorage.getItem("token")}`,
				},
			});
			handleShowToast("Data submitted successfully", "success", true);
			resetForm();
			setSelectedDocuments([]);
			setDocuments([]);
			setComment("");
			setSelectedSkillId("");
		} catch (error) {
			const axiosError = error as AxiosError<{ error?: string }>;
			const errorMessage =
				axiosError.response?.data?.error ||
				axiosError.response?.data?.toString() ||
				"An error occurred";
			handleShowToast(errorMessage, "error", true);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const handleSkillChange = useCallback(
		(setFieldValue: (field: string, value: any) => void) =>
			(event: any, newValue: Skill | null) => {
				setFieldValue("skill", newValue);
				if (!newValue) {
					setDocuments([]);
					setSelectedDocuments([]);
					setSelectedSkillId("");
				}
				if (newValue) {
					fetchDocuments(newValue.skill_id);
					setSelectedSkillId(newValue.skill_id);
				}
			},
		[]
	);

	const handleToggleDocument = (docId: string) => {
		setSelectedDocuments((prev) =>
			prev.includes(docId)
				? prev.filter((id) => id !== docId)
				: [...prev, docId]
		);
	};

	const handleSelectAll = () => {
		if (selectedDocuments.length === documents.length) {
			setSelectedDocuments([]);
		} else {
			setSelectedDocuments(documents.map((d) => d.document_id));
		}
	};

	const handleDeleteDocumentClick = async (docId: string) => {
		setDocumentToDelete(docId);
		setAssignedEmployeeCount(null);
		setIsFetchingCount(true);
		setDeleteDialogOpen(true);
		try {
			const res = await axios.get(
				`${documentAssignmentCount}?document_id=${docId}`,
				{ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
			);
			setAssignedEmployeeCount(res.data.assigned_count ?? 0);
		} catch {
			setAssignedEmployeeCount(0);
		} finally {
			setIsFetchingCount(false);
		}
	};

	const handleConfirmDeleteDocument = async () => {
		if (!documentToDelete) return;
		setIsDeletingDocument(true);
		try {
			await axios.delete(
				`${deleteSkillDocument}?document_id=${documentToDelete}`,
				{ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
			);
			setDocuments((prev) =>
				prev.filter((d) => d.document_id !== documentToDelete)
			);
			setSelectedDocuments((prev) =>
				prev.filter((id) => id !== documentToDelete)
			);
			handleShowToast("Document deleted successfully", "success", true);
		} catch {
			handleShowToast("Error deleting document", "error", true);
		} finally {
			setIsDeletingDocument(false);
			setDeleteDialogOpen(false);
			setDocumentToDelete(null);
			setAssignedEmployeeCount(null);
		}
	};

	async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
		if (!event.target.files?.length) return;
		const files = Array.from(event.target.files);
		setIsDocumentUploading(true);
		try {
			const uploadPromises = files.map(async (file) => {
				const formData = new FormData();
				formData.append("files", file);
				formData.append("skill", selectedSkillId);
				return axios.post(skillDocumentUpload, formData, {
					headers: {
						"Content-Type": "multipart/form-data",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				});
			});
			await Promise.all(uploadPromises);
			handleShowToast("File(s) uploaded successfully", "success", true);
			fetchDocuments(selectedSkillId);
		} catch {
			handleShowToast("Error uploading files", "error", true);
		} finally {
			setIsDocumentUploading(false);
			event.target.value = "";
		}
	}

	if (isLoading) {
		return <Loading isLoading={isLoading} />;
	}

	const initialValues: FormValues = {
		skill: null,
		employee: [],
		date: null,
		comment: "",
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ values, setFieldValue, errors, touched, submitCount, setFieldTouched }) => (
					<Box
						sx={{
							maxWidth: 860,
							mx: "auto",
							mt: 2,
							mb: 6,
							px: { xs: 2, sm: 3 },
						}}
					>
						<Form>
							{/* ── Form Fields Card ─────────────────────────── */}
							<Paper
								elevation={0}
								sx={{
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 2,
									p: { xs: 2.5, sm: 3 },
									mb: 3,
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 2.5, color: "text.primary" }}
								>
									Assignment Details
								</Typography>

								<Grid container spacing={2.5}>
									<Grid item xs={12} sm={6}>
										<Autocomplete<Skill, false>
											options={skills}
											getOptionLabel={(option) => option?.skill_name || ""}
											value={values.skill}
											onChange={handleSkillChange(setFieldValue)}
											loading={isSkillLoading}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Select Skill"
													size="small"
													error={touched.skill && Boolean(errors.skill)}
													helperText={touched.skill && errors.skill}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<>
																{isSkillLoading ? (
																	<CircularProgress color="inherit" size={16} />
																) : null}
																{params.InputProps.endAdornment}
															</>
														),
													}}
												/>
											)}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<Autocomplete<Employee, true>
											multiple
											options={employees}
											getOptionLabel={(option) =>
												option
													? `${option.first_name} ${option.last_name}`
													: ""
											}
											value={values.employee}
											onChange={(_, newValue) =>
												setFieldValue("employee", newValue)
											}
											loading={isEmployeeLoading}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Select Employees"
													size="small"
													error={
														touched.employee && Boolean(errors.employee)
													}
													helperText={
														touched.employee &&
														errors.employee?.toString()
													}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<>
																{isEmployeeLoading ? (
																	<CircularProgress color="inherit" size={16} />
																) : null}
																{params.InputProps.endAdornment}
															</>
														),
													}}
												/>
											)}
											renderTags={(tagValue, getTagProps) =>
												tagValue.map((option, index) => (
													<Chip
														label={`${option.first_name} ${option.last_name}`}
														{...getTagProps({ index })}
														size="small"
														deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
														onDelete={() => {
															const newValue = values.employee.filter(
																(emp) => emp.id !== option.id
															);
															setFieldValue("employee", newValue);
														}}
													/>
												))
											}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<DatePicker
											label="Completion Date"
											value={values.date}
											minDate={new Date()}
											format="dd/MM/yyyy"
											onChange={(newValue) => {
												setFieldValue("date", newValue);
												setFieldTouched("date", true, false);
											}}
											slotProps={{
												textField: {
													size: "small",
													fullWidth: true,
													error:
														(touched.date || submitCount > 0) &&
														Boolean(errors.date),
													helperText:
														(touched.date || submitCount > 0) &&
														errors.date,
													placeholder: "DD/MM/YYYY",
												},
											}}
										/>
									</Grid>
								</Grid>
							</Paper>

							{/* ── Documents Card (shown when skill selected) ── */}
							{values.skill && (
								<Paper
									elevation={0}
									sx={{
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 2,
										mb: 3,
										overflow: "hidden",
									}}
								>
									{/* Header */}
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											px: 3,
											py: 2,
											borderBottom: "1px solid",
											borderColor: "divider",
											backgroundColor: "grey.50",
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography variant="subtitle1" fontWeight={600}>
												Documents
											</Typography>
											{documents.length > 0 && (
												<Chip
													label={documents.length}
													size="small"
													color="primary"
													variant="outlined"
													sx={{ height: 20, fontSize: 11 }}
												/>
											)}
										</Box>

										<label htmlFor="upload-file">
											<Button
												variant="contained"
												size="small"
												component="span"
												startIcon={
													isDocumentUploading ? (
														<CircularProgress size={14} color="inherit" />
													) : (
														<UploadFileIcon fontSize="small" />
													)
												}
												disabled={isDocumentUploading || isFormSubmitting}
												sx={{ textTransform: "none", borderRadius: 1.5 }}
											>
												{isDocumentUploading ? "Uploading…" : "Upload Document"}
												<input
													type="file"
													id="upload-file"
													multiple
													style={{ display: "none" }}
													onChange={handleFileUpload}
												/>
											</Button>
										</label>
									</Box>

									{/* Body */}
									<Box sx={{ p: 2, minHeight: 120 }}>
										{documentLoading ? (
											<Box
												sx={{
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
													py: 4,
												}}
											>
												<CircularProgress size={28} />
											</Box>
										) : documents.length === 0 ? (
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													py: 5,
													color: "text.disabled",
													gap: 1,
												}}
											>
												<InsertDriveFileOutlinedIcon sx={{ fontSize: 40 }} />
												<Typography variant="body2">
													No documents uploaded yet
												</Typography>
												<Typography variant="caption">
													Click "Upload Document" to add files for this skill
												</Typography>
											</Box>
										) : (
											<>
												{/* Select-all row */}
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														px: 1,
														py: 0.5,
														mb: 0.5,
													}}
												>
													<Checkbox
														size="small"
														checked={
															documents.length > 0 &&
															selectedDocuments.length === documents.length
														}
														indeterminate={
															selectedDocuments.length > 0 &&
															selectedDocuments.length < documents.length
														}
														onChange={handleSelectAll}
													/>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ ml: 0.5 }}
													>
														{selectedDocuments.length > 0
															? `${selectedDocuments.length} of ${documents.length} selected`
															: "Select all"}
													</Typography>
												</Box>
												<Divider sx={{ mb: 0.5 }} />

											{/* Document rows */}
											{documents.map((doc) => (
												<Box
													key={doc.document_id}
													onClick={() => handleToggleDocument(doc.document_id)}
													sx={{
														display: "flex",
														alignItems: "center",
														px: 1,
														py: 0.75,
														borderRadius: 1,
														cursor: "pointer",
														transition: "background 0.15s",
														"&:hover": {
															backgroundColor: "action.hover",
														},
														backgroundColor: selectedDocuments.includes(
															doc.document_id
														)
															? "rgba(25, 118, 210, 0.06)"
															: "transparent",
													}}
												>
													<Checkbox
														size="small"
														checked={selectedDocuments.includes(
															doc.document_id
														)}
														onChange={() =>
															handleToggleDocument(doc.document_id)
														}
														onClick={(e) => e.stopPropagation()}
													/>
														<InsertDriveFileOutlinedIcon
															sx={{
																fontSize: 18,
																color: "text.secondary",
																mr: 1,
																ml: 0.5,
															}}
														/>
														<Typography
															variant="body2"
															sx={{
																flex: 1,
																overflow: "hidden",
																textOverflow: "ellipsis",
																whiteSpace: "nowrap",
															}}
														>
															{doc.document_name}
														</Typography>
														<Tooltip title="Delete document" arrow>
															<IconButton
																size="small"
																color="error"
																onClick={(e) => {
																	e.stopPropagation();
																	handleDeleteDocumentClick(doc.document_id);
																}}
																sx={{
																	ml: 1,
																	opacity: 0.7,
																	"&:hover": { opacity: 1 },
																}}
															>
																<DeleteOutlineIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													</Box>
												))}
											</>
										)}
									</Box>
								</Paper>
							)}

							{/* ── Comment Box Card ─────────────────────────── */}
							{values.skill && documents.length > 0 && (
								<Paper
									elevation={0}
									sx={{
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 2,
										overflow: "hidden",
										mb: 3,
									}}
								>
									<Box
										sx={{
											px: 3,
											py: 1.5,
											borderBottom: "1px solid",
											borderColor: "divider",
											backgroundColor: "grey.50",
										}}
									>
										<Typography variant="subtitle2" fontWeight={600}>
											Comment{" "}
											<Typography
												component="span"
												variant="caption"
												color="text.secondary"
											>
												(optional)
											</Typography>
										</Typography>
									</Box>
									<Box sx={{ p: 2 }}>
										<SkillDocumentCommentBox
											value={comment}
											onChange={setComment}
											placeholder="Add notes or instructions for the employees…"
											disabled={isFormSubmitting}
											compact
										/>
									</Box>
								</Paper>
							)}

							{/* ── Submit button ─────────────────────────────── */}
							<Stack direction="row" justifyContent="flex-end">
								<Button
									variant="contained"
									color="primary"
									type="submit"
									disabled={isFormSubmitting}
									endIcon={
										isFormSubmitting ? (
											<CircularProgress size={16} color="inherit" />
										) : (
											<SendIcon fontSize="small" />
										)
									}
									sx={{
										textTransform: "none",
										borderRadius: 1.5,
										px: 3.5,
										py: 1,
										fontWeight: 600,
									}}
								>
									{isFormSubmitting ? "Submitting…" : "Submit"}
								</Button>
							</Stack>
						</Form>
					</Box>
				)}
			</Formik>

			{/* ── Delete confirmation dialog ──────────────── */}
			<Dialog
				open={deleteDialogOpen}
				onClose={() => {
					if (!isDeletingDocument) {
						setDeleteDialogOpen(false);
						setDocumentToDelete(null);
						setAssignedEmployeeCount(null);
					}
				}}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>
					{assignedEmployeeCount !== null && assignedEmployeeCount > 0
						? "Document In Use"
						: "Delete Document"}
				</DialogTitle>
				<DialogContent>
					{isFetchingCount ? (
						<Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
							<CircularProgress size={16} />
							<DialogContentText>Checking assignments…</DialogContentText>
						</Stack>
					) : assignedEmployeeCount !== null && assignedEmployeeCount > 0 ? (
						<DialogContentText>
							This document is currently assigned to{" "}
							<strong>
								{assignedEmployeeCount} employee
								{assignedEmployeeCount !== 1 ? "s" : ""}
							</strong>
							. They will no longer be able to download it. Are you sure you
							want to delete this document?
						</DialogContentText>
					) : (
						<DialogContentText>
							Are you sure you want to delete this document? This action cannot
							be undone.
						</DialogContentText>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setDeleteDialogOpen(false);
							setDocumentToDelete(null);
							setAssignedEmployeeCount(null);
						}}
						disabled={isDeletingDocument || isFetchingCount}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmDeleteDocument}
						color="error"
						variant="contained"
						disabled={isDeletingDocument || isFetchingCount}
						startIcon={
							isDeletingDocument ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<DeleteOutlineIcon fontSize="small" />
							)
						}
					>
						{isDeletingDocument ? "Deleting…" : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</LocalizationProvider>
	);
};

export default SkillDocument;
